import { BranchType } from "@/features/church/config/branches/types/branch.types"
import { type BranchSummary } from "@/features/auth/services/user-branch-access-store"
import { PARISH_BRANCH_SLUG } from "@/shared/lib/routing/reserved-slugs"

type TenantFormBranchContextInput = {
  accessibleBranches: BranchSummary[]
  activeBranchSlug: string
  showMultiBranchWorkspaceUi: boolean
}

export type TenantFormBranchContextState = {
  activeBranch: BranchSummary | null
  activeBranchId: string | null
  activeBranchSlug: string
  accessibleBranches: BranchSummary[]
  normalBranches: BranchSummary[]
  systemBranch: BranchSummary | null
  isParishContext: boolean
  isMonobranchUi: boolean
  shouldHideBranchField: boolean
  shouldShowBranchField: boolean
}

export function getTenantFormBranchContext(input: TenantFormBranchContextInput): TenantFormBranchContextState {
  const normalBranches = input.accessibleBranches.filter((branch) => branch.branchType !== BranchType.System)
  const systemBranch = input.accessibleBranches.find((branch) => branch.branchType === BranchType.System) ?? null

  /*
   * `parish` in the URL means "this workspace's own context". In a MULTI-branch workspace that is the parish
   * console; in a MONO workspace it is the single operational branch, because a mono church's parish is
   * invisible and everything belongs to that branch. Resolved explicitly rather than left to the
   * `accessibleBranches[0]` fallback below, which answered the question by row order.
   */
  const contextBranch = input.showMultiBranchWorkspaceUi ? systemBranch : normalBranches[0] ?? null
  const activeBranch =
    (input.activeBranchSlug === PARISH_BRANCH_SLUG ? contextBranch : null) ??
    input.accessibleBranches.find((branch) => branch.slug === input.activeBranchSlug) ??
    input.accessibleBranches[0] ??
    null

  const isParishContext = activeBranch?.branchType === BranchType.System
  const isMonobranchUi = !input.showMultiBranchWorkspaceUi || normalBranches.length <= 1
  const shouldHideBranchField = isMonobranchUi

  return {
    activeBranch,
    activeBranchId: activeBranch?.id ?? null,
    activeBranchSlug: activeBranch?.slug ?? input.activeBranchSlug,
    accessibleBranches: input.accessibleBranches,
    normalBranches,
    systemBranch,
    isParishContext,
    isMonobranchUi,
    shouldHideBranchField,
    shouldShowBranchField: !shouldHideBranchField,
  }
}
